import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, Card, CardContent, Button, TextField, Tooltip, IconButton, CircularProgress, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableHead, TableRow, TableCell, TableContainer, LinearProgress, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VideoLibrary as VideoIcon,
  Upload as UploadIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { videosAPI } from '../services/api';
import { log } from '../utils/logger';
import MainLayout from '../components/MainLayout';

const VideosPage: React.FC = () => {
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [currentVideo, setCurrentVideo] = useState<any>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    slug: '',
    processingMode: 'high' // 'high' veya 'fast'
  });

  useEffect(() => {
    // Mock veri yerine gerçek API'yi kullan
    const fetchVideos = async () => {
      try {
        log('Videoları getiriliyor...');
        const response = await videosAPI.getAll();
        log('Video yanıtı:', response);
        setVideos(response.data);
        setLoading(false);
      } catch (error) {
        log('Video getirme hatası:', error);
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const handleOpenEditDialog = (video: any) => {
    setCurrentVideo(video);
    setEditOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditOpen(false);
    setCurrentVideo(null);
  };

  const handleOpenUploadDialog = () => {
    setUploadOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setUploadOpen(false);
    setUploadFile(null);
    setUploadForm({ title: '', slug: '', processingMode: 'high' });
    setProcessing(false);
    setUploading(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      setUploadFile(file);
      setUploadForm(prev => ({
        ...prev,
        title: file.name.replace(/\.[^/.]+$/, ''),
        slug: file.name.toLowerCase().replace(/\.[^/.]+$/, '').replace(/[^a-z0-9-]/g, '-')
      }));
    }
  };

  const [processing, setProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);

  const pollStatus = async (slug: string) => {
    setProcessing(true);
    setProcessingProgress(0);
    const interval = setInterval(async () => {
      try {
        const response = await videosAPI.getStatus(slug);

        // Progress ve Error kontrolü
        if (response.data.status === 'error') {
          clearInterval(interval);
          setProcessing(false);
          setUploading(false);
          alert('Video işlenirken bir hata oluştu. Lütfen dosyanızı kontrol edip tekrar deneyin. (Bozuk veya desteklenmeyen format)');
          return;
        }

        if (response.data.progress) {
          setProcessingProgress(response.data.progress);
        }

        if (response.data.status === 'ready') {
          clearInterval(interval);
          setProcessing(false);
          setProcessingProgress(100);

          // Listeyi güncelle ve dialogu kapat
          const newVideo = {
            id: videos.length + 1, // Gerçek ID backendden gelmeli ama geçici olarak
            title: uploadForm.title,
            slug: slug,
            created_at: new Date().toISOString()
          };
          // En garantisi backendden listeyi yeniden çekmek
          const allVideos = await videosAPI.getAll();
          setVideos(allVideos.data);

          handleCloseUploadDialog();
        }
      } catch (error) {
        log('Status kontrol hatası:', error);
      }
    }, 1000); // 1 saniyede bir kontrol et (daha sık güncelleme için)
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('slug', uploadForm.slug);
      formData.append('processingMode', uploadForm.processingMode);
      formData.append('video', uploadFile);

      log('Yükleme isteği gönderiliyor...');
      // Bu istek artık 202 dönüyor, yani "işlem başladı"
      const uploadResponse = await videosAPI.upload(formData, (progress) => {
        setUploadProgress(progress);
      });

      setUploading(false);
      setUploadProgress(0);

      // Polling başlat - SERVER'DAN DÖNEN SLUG'I KULLAN!
      const actualSlug = uploadResponse.data?.video?.slug || uploadForm.slug;
      log('Polling başlatılıyor, slug:', actualSlug);
      await pollStatus(actualSlug);

    } catch (error) {
      log('Yükleme hatası:', error);
      setUploading(false);
      setProcessing(false);
      alert('Yükleme sırasında hata oluştu.');
    } finally {
      // Loading kapatmıyoruz, processing başladı
    }
  };

  const handleSaveVideo = async () => {
    if (!currentVideo) return;

    try {
      // Slug değişmiş mi kontrol et
      const updatedData = {
        title: currentVideo.title,
        slug: currentVideo.slug
      };

      await videosAPI.update(currentVideo.id, updatedData);

      // Listeyi güncelle
      const updatedVideos = videos.map(v =>
        v.id === currentVideo.id ? { ...v, ...updatedData } : v
      );
      setVideos(updatedVideos);
      handleCloseEditDialog();
    } catch (error) {
      log('Güncelleme hatası:', error);
      alert('Video güncellenirken bir hata oluştu.');
    }
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<number | null>(null);

  const handleDeleteClick = (id: number) => {
    setVideoToDelete(id);
    setDeleteOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteOpen(false);
    setVideoToDelete(null);
  };

  const confirmDeleteVideo = async () => {
    if (!videoToDelete) return;

    try {
      await videosAPI.delete(videoToDelete);
      setVideos(videos.filter(v => v.id !== videoToDelete));
      handleCloseDeleteDialog();
    } catch (error) {
      log('Silme hatası:', error);
      alert('Video silinirken bir hata oluştu');
    }
  };

  const handlePreview = (slug: string) => {
    // Önizleme için dummy token ile yeni sekmede aç
    // Gerçek uygulamada 'preview' adında özel bir token veya admin'e ait bir token kullanılabilir
    // Backend'de token doğrulaması yapıldığı için, geçerli bir token lazım
    // Bu yüzden admin panelinden tıklandığında, admin yetkisiyle izleme sayfasına gidilebilir
    // Veya geçici olarak:
    const previewUrl = `https://video.banasesver.com/watch/${slug}?t=preview_admin_token`;
    window.open(previewUrl, '_blank');
  };

  return (
    <>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Videolar
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Video yükleme, düzenleme ve silme işlemleri için aşağıdaki formu kullanın.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Başlık</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Oluşturulma</TableCell>
                    <TableCell>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {videos.map((video) => (
                    <TableRow key={video.id}>
                      <TableCell>{video.title}</TableCell>
                      <TableCell>{video.slug}</TableCell>
                      <TableCell>{new Date(video.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Önizle">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handlePreview(video.slug)}
                            >
                              <PreviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditDialog(video)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(video.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenUploadDialog}
          sx={{ mt: 2 }}
        >
          Video Ekle
        </Button>

        {/* Video Yükle Dialog */}
        <Dialog open={uploadOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Video Yükle</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Button
                component="label"
                variant="outlined"
                fullWidth
                sx={{ py: 2, borderStyle: 'dashed' }}
              >
                {uploadFile ? uploadFile.name : 'Video Dosyası Seç'}
                <input
                  type="file"
                  hidden
                  accept="video/*"
                  onChange={handleFileChange}
                />
              </Button>
              <TextField
                label="Başlık"
                fullWidth
                value={uploadForm.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUploadForm({ ...uploadForm, title: e.target.value })}
                margin="normal"
              />
              <TextField
                label="Slug"
                fullWidth
                value={uploadForm.slug}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUploadForm({ ...uploadForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                margin="normal"
                helperText="URL için kullanılacak benzersiz kimlik"
              />

              <FormControl component="fieldset" sx={{ mt: 2, border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: 1, p: 2 }}>
                <FormLabel component="legend" sx={{ fontSize: '0.8rem', px: 1, ml: -1 }}>İşleme Kalitesi</FormLabel>
                <RadioGroup
                  aria-label="processing-mode"
                  name="processing-mode"
                  value={uploadForm.processingMode || 'high'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUploadForm({ ...uploadForm, processingMode: e.target.value })}
                >
                  <FormControlLabel
                    value="high"
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Yüksek Kalite (Önerilen)</Typography>
                        <Typography variant="caption" color="text.secondary">Tüm cihazlar için optimize edilir. (720p, 480p, 360p).</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="fast"
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">Hızlı İşlem</Typography>
                        <Typography variant="caption" color="text.secondary">Sadece tek kalite (720p). 3 kat daha hızlıdır.</Typography>
                      </Box>
                    }
                    sx={{ mt: 1 }}
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUploadDialog} disabled={uploading || processing}>
              İptal
            </Button>
            <Button onClick={handleUpload} variant="contained" disabled={uploading || processing}>
              {uploading ? `Yükleniyor %${uploadProgress}` : processing ? 'İşleniyor...' : 'Yükle'}
            </Button>
          </DialogActions>
          {uploading && (
            <Box sx={{ width: '100%', px: 3, pb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                Video yükleniyor...
              </Typography>
            </Box>
          )}
          {processing && (
            <Box sx={{ width: '100%', px: 3, pb: 2, textAlign: 'center' }}>
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="body2" gutterBottom>
                Video işleniyor ve HLS formatına dönüştürülüyor...
              </Typography>
              <LinearProgress variant="determinate" value={processingProgress} sx={{ width: '100%', mb: 1 }} />
              <Typography variant="h6" color="primary">
                %{processingProgress}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Lütfen pencereyi kapatmayın. (Büyük dosyalar için birkaç dakika sürebilir)
              </Typography>
            </Box>
          )}
        </Dialog >

        {/* Video Düzenle Dialog */}
        < Dialog open={editOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth >
          <DialogTitle>Video Düzenle</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Başlık"
                fullWidth
                defaultValue={currentVideo?.title || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentVideo({ ...currentVideo, title: e.target.value })}
                margin="normal"
              />
              <TextField
                label="Slug"
                fullWidth
                defaultValue={currentVideo?.slug || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentVideo({ ...currentVideo, slug: e.target.value })}
                margin="normal"
                helperText="URL için kullanılacak benzersiz kimlik"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>
              İptal
            </Button>
            <Button onClick={handleSaveVideo} variant="contained">
              Kaydet
            </Button>
          </DialogActions>
        </Dialog >
      </Box >

      {/* Delete Confirmation Dialog */}
      < Dialog open={deleteOpen} onClose={handleCloseDeleteDialog} >
        <DialogTitle>Video Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu videoyu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>İptal</Button>
          <Button onClick={confirmDeleteVideo} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
};

export default VideosPage;